import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { PageSkeleton } from 'components/templates/PageSkeleton';
import {
  type GetFeaturesResponse,
  type GetFeaturesVariables,
  getFeatures,
} from 'services/partnersSelfSignup';

import { experimentVariantManager } from 'utils/experimentation';
import {
  defaultValues,
  features,
  transformFeatureResponseInFlagsContext,
} from './utils/features';

type UnleashFlag = {
  isEnabled: boolean;
  isExperiment: false;
};

export type FlagsContext = {
  flags: Record<string, UnleashFlag>;
  sessionId: string;
};

export const FlagsContext = createContext<FlagsContext | null>(null);

export function FlagsProvider({
  children,
  sessionId,
}: PropsWithChildren<{ sessionId: string }>) {
  const contextualizedFeatures = features.map(feature => ({
    ...feature,
    context: { sessionId },
  }));

  const { data, isPending, isSuccess } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['features', sessionId],
    queryFn: async () => {
      const result = await getFeatures(contextualizedFeatures);

      features.forEach(feature => {
        const isEnabled = result[feature.name];

        if (isEnabled === undefined) {
          return;
        }

        experimentVariantManager.upsertDecision({
          key: feature.name,
          value: Boolean(isEnabled),
          source: 'unleash',
        });
      });

      return result;
    },
    retry: false,
    refetchOnWindowFocus: false,
    select: transformFeatureResponseInFlagsContext,
    meta: { persist: false },
  });

  const values = useMemo(() => {
    return {
      flags: isSuccess ? data : defaultValues,
      sessionId,
    };
  }, [data, isSuccess, sessionId]);

  const isLoading = isPending;

  return (
    <FlagsContext.Provider value={values}>
      {isLoading ? <PageSkeleton /> : children}
    </FlagsContext.Provider>
  );
}

export function useFlags() {
  const flagsContext = useContext(FlagsContext);

  if (!flagsContext) {
    throw new Error('useFlags should be called inside FlagsProvider');
  }

  return flagsContext.flags;
}

export function flagQueryOptions(
  feature: GetFeaturesVariables[number],
  customOptions?: Omit<
    UseQueryOptions<GetFeaturesResponse>,
    'queryKey' | 'queryFn'
  >,
): UseQueryOptions<GetFeaturesResponse> {
  return {
    ...customOptions,
    queryKey: [feature],
    queryFn: () => getFeatures([feature]),
    retry: false,
    refetchOnWindowFocus: false,
  };
}

export function useFlag(
  feature: GetFeaturesVariables[number],
  options?: Omit<UseQueryOptions<GetFeaturesResponse>, 'queryKey' | 'queryFn'>,
) {
  const { data, isPending } = useQuery(flagQueryOptions(feature, options));

  return {
    isLoading: isPending,
    isEnabled: data ? data[feature.name] : feature.fallbackValue,
  };
}
